import React, { FunctionComponent, useState, ChangeEvent, useMemo, useEffect } from "react"

import Input, { IInputProps } from "components/Input/Input"
import { formatNumber, stringToNumber, getDecimalSeparator, countDecimals } from "utils/utils"
import { INT32_MAX_VALUE } from "config/constants"

interface IInputNumberProps extends Omit<IInputProps, "type" | "onChange"> {
    minDecimals?: number
    maxDecimals?: number
    isSubmitted?: boolean
    onChange?(value: string): void
}

const InputNumber: FunctionComponent<React.PropsWithChildren<IInputNumberProps>> = ({
    minDecimals,
    maxDecimals,
    isSubmitted,
    onChange,
    ...restProps
}) => {
    const {
        value: initValue,
        isInvalid,
        onFocus,
        onBlur,
    } = restProps
    const [value, setValue] = useState(initValue?.toString() || "")
    const [isFocused, setIsFocused] = useState(false)

    useEffect(() => {
        setValue(initValue?.toString() || "")
    }, [initValue])

    const currentValue = useMemo<IInputProps["value"]>(() => {
        if (isFocused || isInvalid || !value) {
            return value
        }
        const floatValue = stringToNumber(value)
        if (floatValue === null || floatValue > INT32_MAX_VALUE || floatValue < -INT32_MAX_VALUE) {
            return value
        }
        return formatNumber(floatValue, minDecimals || 0, maxDecimals)
    }, [isFocused, isInvalid, value, minDecimals, maxDecimals])

    function handleInputFocus(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
        setIsFocused(true)
        if (typeof onFocus === "function") {
            onFocus(event)
        }
    }

    function handleBlur(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
        setIsFocused(false)
        if (typeof onBlur === "function") {
            onBlur(e)
        }
    }

    function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
        e.target.value = e.target.value.trim()
        const decimalSeparator = getDecimalSeparator()
        const wrongDecimalSeparator = decimalSeparator === "." ? "," : "."
        if (e.target.value !== "" && typeof maxDecimals !== "undefined" && countDecimals(e.target.value) > maxDecimals && !e.target.value.includes(wrongDecimalSeparator)) {
            const floatValue = stringToNumber(e.target.value)
            if (floatValue !== null) {
                e.target.value = floatValue.toFixed(maxDecimals).replace(".", decimalSeparator)
            }
        }
        setValue(e.target.value)
        if (typeof onChange === "function") {
            onChange(e.target.value)
        }
    }

    return (
        <Input
            {...restProps}
            isInvalid={isSubmitted && isInvalid}
            value={currentValue}
            onFocus={handleInputFocus}
            onBlur={handleBlur}
            onChange={handleChange}
            />
    )
}

export default InputNumber
